import { v4 as uuidv4 } from 'uuid';
export function createDefaultRecurrence() {
    return {
        frequency: null,
        days_of_week: [],
        time_of_day: null,
        end_date: null,
    };
}
export function createTask(data) {
    const now = new Date().toISOString();
    return {
        id: uuidv4(),
        project_id: data.project_id,
        parent_task_id: data.parent_task_id ?? null,
        title: data.title,
        description: data.description ?? null,
        priority: data.priority ?? 3,
        difficulty: data.difficulty ?? 3,
        energy_level: data.energy_level ?? 'medium',
        estimated_minutes: data.estimated_minutes ?? 60,
        actual_minutes: null,
        status: 'todo',
        dependencies: data.dependencies ?? [],
        tags: data.tags ?? [],
        deadline: data.deadline ?? null,
        scheduled_for: data.scheduled_for ?? null,
        created_at: now,
        updated_at: now,
        completed_at: null,
        is_habit: data.is_habit ?? false,
        recurrence: {
            ...createDefaultRecurrence(),
            ...data.recurrence,
        },
        is_paused: false,
        paused_until: null,
        is_archived: false,
        archived_at: null,
        needs_review: data.needs_review ?? false,
        is_pinned: data.is_pinned ?? false,
        pin_type: data.pin_type ?? 'soft',
        target_count: data.target_count ?? null,
        current_count: data.target_count ? 0 : null,
        count_unit: data.count_unit ?? null,
        ...(data.habit_kind ? { habit_kind: data.habit_kind } : {}),
    };
}
/**
 * A task with an update applied.
 *
 * The one place a stored Task is merged with a partial change. Callers
 * previously spread `{ ...existing, ...patch, updated_at }` themselves, which
 * is fine until two of them do it slightly differently — one forgetting the
 * timestamp, another letting `undefined` through and blanking a field the
 * caller never mentioned.
 *
 * Only keys actually PRESENT are applied. `undefined` means "not mentioned"
 * and leaves the stored value alone; `null` is a real value and clears the
 * field. That distinction is the whole reason this isn't a plain spread.
 */
export function applyTaskUpdate(task, update, now) {
    // The cast is contained to this one line: Task is an interface, so it has
    // no index signature, but the merge is by definition key-by-key.
    const next = { ...task };
    for (const [key, value] of Object.entries(update)) {
        if (value === undefined)
            continue;
        /**
         * `JSON.parse` happily creates an own property called `__proto__`, and
         * assigning to it here would change the object's prototype instead of
         * setting a field. No caller can currently reach this — the tool maps a
         * fixed list of keys and the form is typed — but this function is
         * exported from core and the next caller may hand it parsed JSON
         * directly. One line, and the class of bug is gone rather than merely
         * unreachable.
         */
        if (key === '__proto__' || key === 'constructor' || key === 'prototype')
            continue;
        if (key === 'recurrence') {
            // Merged, not replaced: a caller changing only the day of the month
            // must not blank the time of day.
            next.recurrence = { ...next.recurrence, ...value };
            continue;
        }
        next[key] = value;
    }
    next.updated_at = now;
    return next;
}
/**
 * A habit retired, or brought back.
 *
 * Archiving clears any pause as well. Without that a habit can be both
 * archived and paused, and it then gets counted as "paused" by one screen and
 * "archived" by another — the double-count this deliberately prevents.
 */
export function taskAfterArchive(task, archived, now) {
    return {
        ...task,
        is_archived: archived,
        archived_at: archived ? now : null,
        is_paused: archived ? false : task.is_paused,
        paused_until: archived ? null : task.paused_until,
        updated_at: now,
    };
}
/**
 * A countable task's counter, stepped.
 *
 * Clamped at zero, and completion stays a deliberate act: reaching the target
 * fills the bar but does NOT complete the task. That restraint is the whole
 * rule — auto-completing on the last increment takes the decision away from
 * the user and pays XP they didn't ask for.
 *
 * Here rather than in the hook because the assistant steps counters too
 * ("read two more papers"), and a second `Math.max(0, ...)` somewhere else is
 * how the two paths end up disagreeing about what a negative step does.
 */
export function steppedCount(task, delta) {
    return Math.max(0, (task.current_count ?? 0) + delta);
}
/**
 * Derived progress for a single task, 0-100.
 *
 * - completed → 100, regardless of counts (the user said it's done)
 * - countable (target_count > 0) → current/target, capped at 100
 * - plain task → 0 until completed (binary, the pre-existing behaviour)
 *
 * Every surface that turns tasks into a percentage (project bars, goal
 * roll-ups) uses this, so a task at 2/5 papers is worth 40% everywhere
 * rather than 0% in one place and something else in another.
 */
export function taskProgress(task) {
    if (task.status === 'completed')
        return 100;
    const target = task.target_count ?? 0;
    if (target > 0) {
        const current = Math.max(0, task.current_count ?? 0);
        return Math.min(100, (current / target) * 100);
    }
    return 0;
}
//# sourceMappingURL=task.js.map